import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { DeviceDetectorService } from 'ngx-device-detector';

const DEV_MOCK_COORDINATES: GeolocationResult = { lat: -34.6037, lng: -58.3816 };

export type GeolocationResult = {
  lat: number;
  lng: number;
} | null;

@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private readonly environmentService = inject(EnvironmentService);
  private readonly deviceService = inject(DeviceDetectorService);

  lastGeolocationResult: GeolocationResult = null;

  private get isInstagramBrowser(): boolean {
    return this.deviceService.userAgent().includes('Instagram');
  }

  get isAvailable(): boolean {
    if (this.environmentService.isLocalEnv) {
      return true;
    }
    return typeof navigator !== 'undefined' && !!navigator.geolocation && !this.isInstagramBrowser;
  }

  async isPermissionGranted(): Promise<boolean> {
    if (this.environmentService.isLocalEnv) {
      return true;
    }
    if (!this.isAvailable) {
      return false;
    }
    const status = await navigator.permissions?.query({ name: 'geolocation' }).catch(() => null);

    return status?.state === 'granted';
  }

  geolocate(): Promise<GeolocationResult> {
    if (!this.isAvailable) {
      return Promise.resolve(null);
    }
    if (this.lastGeolocationResult) {
      return Promise.resolve(this.lastGeolocationResult);
    }

    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.lastGeolocationResult = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          resolve(this.lastGeolocationResult);
        },
        error => {
          if (this.environmentService.isLocalEnv) {
            console.warn('Geolocation failed, using mock coordinates:', error.message);

            this.lastGeolocationResult = DEV_MOCK_COORDINATES;
            resolve(DEV_MOCK_COORDINATES);
          } else {
            console.error(error);

            resolve(null);
          }
        },
        { timeout: 5000 },
      );
    });
  }
}
